<script setup>
import { computed, ref, watch } from 'vue';
import { formatAttributeListHtml } from '../../../shared/html-text.js';

const props = defineProps({
  attributes: { type: Array, default: () => [] },
  descriptionHtml: { type: String, default: '' },
  reviewCount: { type: Number, default: 0 },
  reviewStatus: { type: Boolean, default: true },
  phone: { type: String, default: '0345 - 502615' },
  helpCta: { type: String, default: 'of reserveer direct online.' }
});

const activeTab = ref('specs');

const tabs = computed(() => {
  const list = [];
  if (props.attributes.length) list.push({ id: 'specs', label: 'Specificaties' });
  if (props.descriptionHtml) list.push({ id: 'description', label: 'Beschrijving' });
  list.push({ id: 'help', label: 'Hulp & informatie' });
  if (props.reviewStatus) {
    list.push({
      id: 'reviews',
      label: props.reviewCount > 0 ? `Reviews (${props.reviewCount})` : 'Reviews'
    });
  }
  return list;
});

watch(
  tabs,
  (list) => {
    if (!list.length) return;
    if (!list.find((t) => t.id === activeTab.value)) {
      activeTab.value = list[0].id;
    }
  },
  { immediate: true }
);

function attributeHtml(text) {
  return formatAttributeListHtml(text);
}
</script>

<template>
  <section class="vsb-product-tabs">
    <div class="vsb-product-tabs__nav" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="vsb-product-tabs__tab"
        :class="{ 'vsb-product-tabs__tab--active': activeTab === tab.id }"
        :aria-selected="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-show="activeTab === 'specs'" class="vsb-product-tabs__panel">
      <dl v-if="attributes.length" class="vsb-product-tabs__spec-grid">
        <div v-for="(attr, i) in attributes" :key="attr.attributeId || i" class="vsb-product-tabs__spec">
          <dt class="vsb-product-tabs__spec-name">{{ attr.name }}</dt>
          <dd class="vsb-product-tabs__spec-value" v-html="attributeHtml(attr.text)" />
        </div>
      </dl>
      <p v-else class="vsb-product-tabs__empty">Geen specificaties beschikbaar.</p>
    </div>

    <div v-show="activeTab === 'description'" class="vsb-product-tabs__panel vsb-product-tabs__panel--html">
      <div v-html="descriptionHtml" />
    </div>

    <div v-show="activeTab === 'help'" class="vsb-product-tabs__panel">
      <p>
        Heeft u vragen over dit product of wilt u advies over de juiste keuze? Bel ons op
        <strong>{{ phone }}</strong> {{ helpCta }}
      </p>
      <p>
        <a href="/index.php?route=information/contact">Neem contact op</a>
        ·
        <a href="/index.php?route=information/information&information_id=12">Veiligheid</a>
      </p>
    </div>

    <div v-show="activeTab === 'reviews'" class="vsb-product-tabs__panel">
      <p v-if="reviewCount > 0">{{ reviewCount }} beoordeling(en) voor dit product.</p>
      <p>
        Reviews worden geladen op de productpagina. Schrijf een review via het formulier onderaan
        of bel voor persoonlijk advies.
      </p>
    </div>
  </section>
</template>

<style scoped>
.vsb-product-tabs {
  margin-top: 1.5rem;
  border-top: 1px solid #e8ecf0;
  padding-top: 1rem;
}

.vsb-product-tabs__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
  border-bottom: 2px solid #e8ecf0;
  margin-bottom: 1.25rem;
}

.vsb-product-tabs__tab {
  background: none;
  border: none;
  padding: 0.65rem 0.15rem;
  margin-bottom: -2px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.vsb-product-tabs__tab--active {
  color: #0b3d91;
  border-bottom-color: #0b3d91;
}

.vsb-product-tabs__spec-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
}

.vsb-product-tabs__spec {
  display: grid;
  grid-template-columns: minmax(140px, 36%) minmax(0, 1fr);
  gap: 0;
}

.vsb-product-tabs__spec-name {
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border-bottom: 1px solid #e8ecf0;
  border-right: 1px solid #e8ecf0;
}

.vsb-product-tabs__spec-value {
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-weight: 400;
  color: #475569;
  line-height: 1.6;
  border-bottom: 1px solid #e8ecf0;
}

.vsb-product-tabs__spec-value :deep(strong),
.vsb-product-tabs__spec-value :deep(b) {
  font-weight: 600;
  color: #0f172a;
}

.vsb-product-tabs__spec-value :deep(p) {
  margin: 0 0 0.5rem;
}

.vsb-product-tabs__spec-value :deep(p:last-child) {
  margin-bottom: 0;
}

.vsb-product-tabs__spec-value :deep(ul),
.vsb-product-tabs__spec-value :deep(ol) {
  margin: 0.35rem 0 0.5rem;
  padding-left: 1.25rem;
}

.vsb-product-tabs__spec-value :deep(.vsb-product-tabs__spec-list) {
  list-style: disc;
  margin: 0;
  padding-left: 1.1rem;
}

.vsb-product-tabs__spec-value :deep(.vsb-product-tabs__spec-list li) {
  margin: 0.15rem 0;
  line-height: 1.5;
}

.vsb-product-tabs__spec:last-child .vsb-product-tabs__spec-name,
.vsb-product-tabs__spec:last-child .vsb-product-tabs__spec-value {
  border-bottom: none;
}

.vsb-product-tabs__panel--html :deep(p) {
  margin: 0 0 0.75rem;
  line-height: 1.6;
  color: #475569;
}

.vsb-product-tabs__empty {
  color: #64748b;
}

@media (max-width: 700px) {
  .vsb-product-tabs__spec {
    grid-template-columns: 1fr;
  }

  .vsb-product-tabs__spec-name {
    border-right: none;
  }
}
</style>
